import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import BackendConnector from '../Utils/BackendConnector';
import Constant from '../Utils/Constant';
import Error500Page from '../Error500/Error500Page';
import NotFoundPage from '../NotFound/NotFoundPage';
import { usePhotoCarousel } from '../Utils/usePhotoCarousel';
import { processError, getErrorPageType } from '../Utils/ErrorHandler';
import { createSafeHTML } from '../Utils/HTMLSanitizer';
import { renderDescriptionWithPhotos } from '../Utils/PhotoRenderer';

const PlacePage = () => {
    const { countryId, placeId } = useParams();
    const navigate = useNavigate();
    const [place, setPlace] = useState(null);
    const [country, setCountry] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [errorStatus, setErrorStatus] = useState(null);
    
    // Utiliser le hook custom pour le carrousel
    const {
        carouselOpen,
        carouselIndex,
        carouselPhotos,
        carouselRef,
        handleTouchStart,
        handleTouchMove,
        handleTouchEnd,
        openCarousel,
        closeCarousel,
        prevPhoto,
        nextPhoto,
        setCarouselPhotos,
    } = usePhotoCarousel();

    useEffect(() => {
        const fetchPlaceData = async () => {
            try {
                setIsLoading(true);
                
                // Récupération des détails du pays
                const countryResponse = await BackendConnector.fetchData('travels/');
                const countryData = countryResponse.find(c => c.id === countryId);
                
                if (!countryData) {
                    throw new Error(Constant.COUNTRY_NOT_FOUND);
                }
                
                setCountry(countryData);
                
                // Récupération des places du pays pour trouver la place spécifique
                const placesData = await BackendConnector.fetchData(`travels/${countryId}/places/`);
                const placeData = placesData.find(p => p.id === placeId);
                
                if (!placeData) {
                    throw new Error(Constant.PLACE_NOT_FOUND);
                }
                
                setPlace(placeData);
                
            } catch (err) {
                console.error('Erreur lors du chargement de la place:', err);
                
                const { error: errorMsg, status } = processError(err, Constant.UNABLE_TO_LOAD_PLACE);
                setErrorStatus(status);
                setError(errorMsg);
            } finally {
                setIsLoading(false);
            }
        };

        if (countryId && placeId) {
            fetchPlaceData();
        }
    }, [countryId, placeId]);

    const handleBackClick = () => {
        navigate(`/travel/${countryId}`);
    };

    if (isLoading) {
        return (
            <div className="not-found-page">
                <div className="not-found-content">
                    <div className="spinner-border text-light" role="status" style={{fontSize: '2rem'}}>
                        <span className="visually-hidden">{Constant.LOADING_PLACE}</span>
                    </div>
                    <p className="not-found-message mt-3">{Constant.LOADING_PLACE}</p>
                </div>
            </div>
        );
    }

    if (error || !place || !country) {
        // Si c'est une erreur 500, afficher la page d'erreur 500
        if (errorStatus === 500) {
            return <Error500Page />;
        }
        
        // Sinon, afficher la page 404 (pour les erreurs 404 ou autres)
        let userMessage = Constant.PLACE_NOT_FOUND;
        let technicalMessage = null;
        if (error && (error.includes('404') || error.toLowerCase().includes('not found'))) {
            userMessage = Constant.PLACE_NOT_FOUND;
            technicalMessage = null;
        } else if (error) {
            userMessage = error;
            technicalMessage = error;
        }
        return (
            <NotFoundPage
                title={"404"}
                message={Constant.ERROR}
                description={userMessage}
                technicalMessage={technicalMessage}
                buttonLabel={Constant.BACK_TO_TRAVEL}
                onBack={handleBackClick}
                iconClass="fas fa-arrow-left me-2"
            />
        );
    }

    return (
        <div className="place-page">
            {/* Header de la place */}
            <div 
                className="place-header"
                style={{
                    backgroundImage: place.cover ? `url(${place.cover})` : 'none'
                }}
            >
                <div className="place-header-overlay">
                    <div className="place-header-top">
                        <button className="btn btn-secondary" onClick={handleBackClick}>
                            <i className="fas fa-arrow-left"></i> {Constant.BACK_TO_TRAVEL}
                        </button>
                    </div>
                    
                    <div className="place-header-content">
                        <h1 className="place-page-title">
                            {place.name}
                        </h1>
                        <div className="place-meta">
                            <span className="place-country">
                                {country.flag || Constant.DEFAULT_COUNTRY_FLAG} {country.name}
                            </span>
                            {place.album && (
                                <span 
                                    className="place-country clickable"
                                    onClick={() => navigate(`/album/${place.album.id}`, { 
                                        state: { 
                                            from: 'place', 
                                            countryId: countryId, 
                                            placeId: placeId 
                                        } 
                                    })}
                                >
                                    <i className="fas fa-images me-1"></i>
                                    {Constant.SEE_ALBUM}
                                </span>
                            )}
                        </div>
                        {place.summary && (
                            <p className="place-summary-header">{place.summary}</p>
                        )}
                    </div>
                </div>
            </div>

            {/* Contenu de la place */}
            <div className="place-content">
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-lg-8">
                            {place.accommodations && place.accommodations.length > 0 && place.accommodations.some(acc => acc.name || acc.description || acc.url || (acc.photos && acc.photos.length > 0)) && (
                                <div className="accommodation-section">
                                    <h3>{Constant.ACCOMMODATION}</h3>
                                    {place.accommodations.map((accommodation) => (
                                        <div key={accommodation.id} className="accommodation-item">
                                            <h4 className="accommodation-name">
                                                {accommodation.url ? (
                                                    <a href={accommodation.url} target="_blank" rel="noopener noreferrer">
                                                        {accommodation.name}
                                                    </a>
                                                ) : (
                                                    accommodation.name
                                                )}
                                            </h4>
                                            {accommodation.description && (
                                                <p className="accommodation-description">{accommodation.description}</p>
                                            )}
                                            {accommodation.photos && accommodation.photos.length > 0 && (
                                                <div className="accommodation-photos">
                                                    {accommodation.photos.sort((a, b) => a.order - b.order).map((photo, index) => (
                                                        <img 
                                                            key={photo.id} 
                                                            src={photo.url} 
                                                            alt={accommodation.name}
                                                            className="accommodation-photo clickable-photo"
                                                            loading="lazy"
                                                            onClick={() => {
                                                                openCarousel(index, accommodation.photos.sort((a, b) => a.order - b.order));
                                                            }}
                                                            style={{cursor: 'pointer'}}
                                                        />
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                            
                            <div className="place-description-section">
                                <h3>{Constant.PLACE_DESCRIPTION}</h3>
                                <div className="place-description-content">
                                    {place.description ? (
                                        renderDescriptionWithPhotos(place.description, place.photos, openCarousel)
                                    ) : (
                                        <p className="article-in-progress">{Constant.ARTICLE_IN_PROGRESS}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Carrousel photos en plein écran */}
            {carouselOpen && carouselPhotos.length > 0 && (
                <div 
                    className="carousel-fullscreen"
                    ref={carouselRef}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                >
                    <button 
                        className="carousel-close-btn"
                        onClick={closeCarousel}
                        aria-label="Fermer la galerie"
                        title="Fermer"
                    >
                        ✕
                    </button>

                    <div className="carousel-container">
                        <img 
                            src={carouselPhotos[carouselIndex].url} 
                            alt={`Photo ${carouselIndex + 1}`}
                            className="carousel-image"
                        />
                        
                        <button 
                            className="carousel-btn prev-btn"
                            onClick={prevPhoto}
                            aria-label="Photo précédente"
                            title="Photo précédente"
                        >
                            ←
                        </button>
                        
                        <button 
                            className="carousel-btn next-btn"
                            onClick={nextPhoto}
                            aria-label="Photo suivante"
                            title="Photo suivante"
                        >
                            →
                        </button>
                    </div>

                    <div className="carousel-controls">
                        <span className="carousel-counter">
                            {carouselIndex + 1} / {carouselPhotos.length}
                        </span>
                    </div>
                    
                    {carouselPhotos[carouselIndex].title && (
                        <div className="carousel-title">
                            {carouselPhotos[carouselIndex].title}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default PlacePage;